import { useState, useCallback, useRef } from "react";
import { ChevronsUpDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useCustomerSearchGraphQL } from "@/hooks/useCustomerSearchGraphQL";
import type { CreatePromiseFormCustomer } from "@/hooks/useCreatePromiseData";

const DEBOUNCE_DELAY_MS = 300;

export interface CustomerSearchMultiSelectProps {
	/** Currently selected customers */
	readonly selectedCustomers: CreatePromiseFormCustomer[];
	/** Called when the selection changes */
	readonly onSelectionChange: (customers: CreatePromiseFormCustomer[]) => void;
	/** Whether the component is disabled */
	readonly disabled?: boolean;
	/** Placeholder text when nothing is selected */
	readonly placeholder?: string;
	/** Maximum number of badges to display before showing "+N more" */
	readonly maxDisplay?: number;
	/** Additional CSS classes for the trigger */
	readonly className?: string;
}

export function CustomerSearchMultiSelect({
	selectedCustomers,
	onSelectionChange,
	disabled = false,
	placeholder = "Select customers...",
	maxDisplay = 3,
	className,
}: CustomerSearchMultiSelectProps) {
	const [open, setOpen] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const {
		customers,
		isLoading,
		isFetchingMore,
		hasMore,
		loadMore,
	} = useCustomerSearchGraphQL(debouncedSearch);

	const handleSearchChange = useCallback((value: string) => {
		setSearchInput(value);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
		debounceTimerRef.current = setTimeout(() => {
			setDebouncedSearch(value);
		}, DEBOUNCE_DELAY_MS);
	}, []);

	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			setOpen(nextOpen);
			if (nextOpen) {
				setSearchInput("");
				setDebouncedSearch("");
			}
		},
		[],
	);

	const handleToggleCustomer = useCallback(
		(customer: CreatePromiseFormCustomer) => {
			const isSelected = selectedCustomers.some((c) => c.id === customer.id);
			if (isSelected) {
				onSelectionChange(selectedCustomers.filter((c) => c.id !== customer.id));
			} else {
				onSelectionChange([...selectedCustomers, customer]);
			}
		},
		[selectedCustomers, onSelectionChange],
	);

	const handleRemove = useCallback(
		(customerId: string) => {
			onSelectionChange(selectedCustomers.filter((c) => c.id !== customerId));
		},
		[selectedCustomers, onSelectionChange],
	);

	const handleClearAll = useCallback(() => {
		onSelectionChange([]);
	}, [onSelectionChange]);

	const displayItems = selectedCustomers.slice(0, maxDisplay);
	const remainingCount = selectedCustomers.length - maxDisplay;

	return (
		<div className="w-full">
			<Popover open={open} onOpenChange={handleOpenChange}>
				<PopoverTrigger asChild>
						<div
							role="button"
							tabIndex={0}
							className={cn(
								"border-input w-full flex items-center justify-between min-h-10 h-auto py-2 px-3 text-sm",
								"border rounded-md bg-background cursor-pointer",
								"hover:bg-accent hover:text-accent-foreground",
								"focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
								disabled && "cursor-not-allowed opacity-50",
								className,
							)}
							onClick={() => { if (!disabled) setOpen(!open); }}
							onKeyDown={(e) => { if (!disabled && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen(!open); } }}
						>
						<div className="flex flex-wrap items-center gap-1 flex-1 min-w-0 pr-2">
							{selectedCustomers.length === 0 ? (
								<span className="text-muted-foreground">{placeholder}</span>
							) : (
								<>
									{displayItems.map((customer) => (
											<button key={customer.id} type="button" onClick={(e) => e.stopPropagation()}>
											<Badge variant="secondary" onClose={() => handleRemove(customer.id)}>
												{customer.name}
											</Badge>
										</button>
									))}
									{remainingCount > 0 && <Badge variant="outline">+{remainingCount} more</Badge>}
								</>
							)}
						</div>
						<ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
					</div>
				</PopoverTrigger>
				<PopoverContent className="w-full p-0" align="start">
					<Command shouldFilter={false}>
						<CommandInput
							placeholder="Search customers..."
							value={searchInput}
							onValueChange={handleSearchChange}
						/>
						<CommandList>
							{!isLoading && customers.length === 0 && (
								<CommandEmpty>No customers found.</CommandEmpty>
							)}
							<CommandGroup>
								{selectedCustomers.length > 0 && (
									<CommandItem onSelect={handleClearAll} className="justify-center text-center">
										Clear all ({selectedCustomers.length})
									</CommandItem>
								)}
								{customers.map((customer) => {
									const isSelected = selectedCustomers.some((c) => c.id === customer.id);
									return (
										<CommandItem
											key={customer.id}
											value={customer.id}
											onSelect={() => handleToggleCustomer(customer)}
										>
											<Checkbox checked={isSelected} className="mr-2" />
											<span>{customer.accountNumber} {customer.name}</span>
										</CommandItem>
									);
								})}
								{isLoading && (
									<CommandItem disabled value="__loading__">
										<span className="text-muted-foreground text-xs">
											Loading...
										</span>
									</CommandItem>
								)}
								{hasMore && !isLoading && (
									<CommandItem
										value="__load_more__"
										onSelect={loadMore}
										className="justify-center"
									>
										<span className="text-xs font-medium text-primary">
											{isFetchingMore ? "Loading..." : "Load more"}
										</span>
									</CommandItem>
								)}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>
		</div>
	);
}
